/**
 * Apply co-located lightmap build-data when a scene is loaded.
 */
import { resourceManager, ResourceType } from '../utils/ResourceManager.js';

import { applyManifestToWorld } from './lightmapApply.js';
import { buildDataZipAssetPathForScene, lightmapBuildDataExists } from './lightmapBuildData.js';

import type { LightmapManifestApplyResult } from './lightmapApply.js';
import type { World } from '../game/World.js';

function logApplyResult(sceneAssetPath: string, result: LightmapManifestApplyResult): void {
  console.log(
    `[lightmapper] Applied lightmaps for ${sceneAssetPath}: ${result.applied.length} object(s) updated` +
      (result.applied.length > 0 ? ` [${result.applied.join(', ')}]` : ''),
  );
  for (const { id, reason } of result.skipped) {
    console.warn(`[lightmapper] Skipped ${id} — ${reason}`);
  }
}

/**
 * Waits for scene meshes/materials/models to finish loading, then applies lightmaps.
 * Use in the editor where material assignment is asynchronous after world deserialization.
 */
export async function tryApplyLightmapsAfterSceneReady(
  world: World,
  sceneAssetPath: string,
): Promise<LightmapManifestApplyResult | null> {
  await resourceManager.waitForResources(ResourceType.All);
  return tryApplyLightmapsToWorld(world, sceneAssetPath);
}

/**
 * Loads and applies baked lightmaps for `sceneAssetPath` when a co-located build-data zip exists.
 * Returns `null` when no zip is present (not an error).
 */
export async function tryApplyLightmapsToWorld(
  world: World,
  sceneAssetPath: string,
): Promise<LightmapManifestApplyResult | null> {
  const zipPath = buildDataZipAssetPathForScene(sceneAssetPath);
  if (!(await lightmapBuildDataExists(zipPath))) {
    return null;
  }

  try {
    const result = await applyManifestToWorld(world, sceneAssetPath);
    logApplyResult(sceneAssetPath, result);
    return result;
  } catch (err) {
    console.error(`[lightmapper] Failed to apply lightmaps for ${sceneAssetPath}:`, err);
    return null;
  }
}
